import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Home, Plus, Calendar, Eye, Trash2 } from 'lucide-react';
import { getWorks, deleteWork } from '../api';
import { toast } from '../hooks/use-toast';

const Work = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [works, setWorks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadWorks();
  }, [location.pathname]);

  const loadWorks = async () => {
    try {
      const data = await getWorks();
      console.log('📋 Works loaded:', data.length);
      setWorks(data);
    } catch (error) {
      console.error('❌ Error loading works:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את דפי העבודה',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק דף עבודה זה?')) {
      try {
        await deleteWork(id);
        setWorks(works.filter(w => w.id !== id));
        toast({
          title: 'הצלחה',
          description: 'דף העבודה נמחק בהצלחה'
        });
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'לא ניתן למחוק את דף העבודה',
          variant: 'destructive'
        });
      }
    }
  };

  // Get today's date in Hebrew
  const getTodayDate = () => {
    return new Date().toLocaleDateString('he-IL', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" dir="rtl">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800">עבודה</h1>
        </div>

        {/* Action Bar with Date and New Button */}
        <div className="max-w-6xl mx-auto mb-6">
          <div className="flex justify-between items-center bg-white rounded-lg shadow-md p-4">
            <Button
              onClick={() => navigate('/work-form')}
              className="px-6 py-3 text-lg font-semibold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
            >
              <Plus className="ml-2 w-6 h-6" />
              יצירת דף עבודה חדש
            </Button>
            
            <div className="flex items-center gap-2 text-gray-700 bg-blue-50 px-6 py-3 rounded-lg">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-lg font-semibold">{getTodayDate()}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-6xl mx-auto">
          <Card className="shadow-xl">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
              <CardTitle className="text-2xl">רשימת דפי עבודה ({works.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              {isLoading ? (
                <div className="text-center text-gray-600">
                  <p className="text-lg">טוען...</p>
                </div>
              ) : works.length === 0 ? (
                <div className="text-center text-gray-600">
                  <p className="text-lg mb-4">אין דפי עבודה עדיין</p>
                  <p>לחץ על "יצירת דף עבודה חדש" כדי להתחיל</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {works.map((work) => (
                    <div
                      key={work.id}
                      className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-800">{work.workName}</h3>
                        <p className="text-gray-600">מזמין: {work.clientName}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(work.date).toLocaleDateString('he-IL')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => navigate('/work-details', { state: { workData: work } })}
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          <Eye className="ml-2 w-4 h-4" />
                          צפייה
                        </Button>
                        <Button
                          onClick={() => handleDelete(work.id)}
                          variant="outline"
                          className="border-red-500 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="ml-2 w-4 h-4" />
                          מחק
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Work;
